import * as React from 'react';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { Diagram } from 'src/containers/ProjectDiagram/types/diagram';
import { isProjectReadOnlySelector } from 'src/selectors/isProjectReadOnly';
import * as collaborationProvider from 'src/utils/collaboration/collaborationProvider';
import { yDocToProject } from 'src/utils/collaboration/scbf/yDocToProject';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

/**
 * Undo/redo for the project diagram (VET-6292).
 *
 * When collaboration is connected, undo/redo is backed by the connection's
 * `Y.UndoManager` (project-wide, tracking only this client's `'local'` edits).
 * `undo()`/`redo()` revert the Y.Doc and then push the reverted project back
 * into Redux via `receivedCollaborationUpdate`, which `useCollaborationDiagramSync`
 * merges into GoJS — reusing the existing remote-update path. The Y.Doc change
 * is also synced to other peers by the Hocuspocus provider.
 *
 * When collaboration is not connected, it falls back to GoJS's built-in
 * `UndoManager` (the legacy single-user path).
 *
 * Returns a normalized shape so callers never branch on connection state.
 */
export const useYjsUndoManager = (diagram: Diagram | undefined) => {
  const { projectId } = useRouterParams();
  const dispatch = useReduxDispatch();

  // Read-only collaborators must not be able to undo/redo: a Yjs
  // `undoManager.undo()` writes a `'local'` change into the Y.Doc and syncs it
  // to peers, bypassing `diagram.isReadOnly` entirely. Gate directly here so
  // the buttons disable the instant access is demoted, without depending on the
  // stack-clear side-effect in `useDiagramReadOnly` (which races a render).
  const isReadOnly = useReduxSelector(isProjectReadOnlySelector(projectId));

  // Re-render when this project's connection status changes so the undo path
  // (Yjs vs GoJS) and the button states stay in sync.
  const [isConnected, setIsConnected] = React.useState(() =>
    collaborationProvider.isConnected(projectId)
  );

  React.useEffect(() => {
    setIsConnected(collaborationProvider.isConnected(projectId));
    return collaborationProvider.addStatusChangeEventListener((changedId) => {
      if (changedId === projectId) {
        setIsConnected(collaborationProvider.isConnected(projectId));
      }
    });
  }, [projectId]);

  const undoManager = isConnected
    ? collaborationProvider.getUndoManager(projectId)
    : undefined;

  const [yjsCanUndo, setYjsCanUndo] = React.useState(false);
  const [yjsCanRedo, setYjsCanRedo] = React.useState(false);

  React.useEffect(() => {
    if (!undoManager) {
      setYjsCanUndo(false);
      setYjsCanRedo(false);
      return undefined;
    }

    const updateCanUndoRedo = () => {
      setYjsCanUndo(undoManager.canUndo());
      setYjsCanRedo(undoManager.canRedo());
    };
    updateCanUndoRedo();

    undoManager.on('stack-item-added', updateCanUndoRedo);
    undoManager.on('stack-item-popped', updateCanUndoRedo);
    undoManager.on('stack-cleared', updateCanUndoRedo);

    return () => {
      undoManager.off('stack-item-added', updateCanUndoRedo);
      undoManager.off('stack-item-popped', updateCanUndoRedo);
      undoManager.off('stack-cleared', updateCanUndoRedo);
    };
  }, [undoManager]);

  // After a Yjs undo/redo reverts the Y.Doc, feed the reverted project into
  // Redux so `useCollaborationDiagramSync` merges it into GoJS.
  const applyYjsDocToRedux = React.useCallback(() => {
    const doc = collaborationProvider.getDoc(projectId);
    if (doc) {
      dispatch(
        projectsActionCreators.receivedCollaborationUpdate(
          projectId,
          yDocToProject(doc)
        )
      );
    }
  }, [dispatch, projectId]);

  const undo = React.useCallback(() => {
    if (isReadOnly) {
      return;
    }
    if (undoManager) {
      undoManager.undo();
      applyYjsDocToRedux();
    } else {
      diagram?.undoManager.undo();
    }
  }, [applyYjsDocToRedux, diagram, isReadOnly, undoManager]);

  const redo = React.useCallback(() => {
    if (isReadOnly) {
      return;
    }
    if (undoManager) {
      undoManager.redo();
      applyYjsDocToRedux();
    } else {
      diagram?.undoManager.redo();
    }
  }, [applyYjsDocToRedux, diagram, isReadOnly, undoManager]);

  const canUndo =
    !isReadOnly &&
    (undoManager ? yjsCanUndo : Boolean(diagram?.undoManager.canUndo()));
  const canRedo =
    !isReadOnly &&
    (undoManager ? yjsCanRedo : Boolean(diagram?.undoManager.canRedo()));

  return { canRedo, canUndo, redo, undo };
};
